import { useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../lib/api'
import {
  Folder, FileCode, Search, RefreshCw, Copy, Download, ChevronRight, ChevronDown, Code
} from 'lucide-react'

interface FileInfo {
  name: string
  path: string
  size?: number
  lastModified?: string
  type?: 'file' | 'folder'
  children?: FileInfo[]
}

interface TreeNode extends FileInfo {
  expanded?: boolean
}

const CODE_EXTENSIONS = ['.ts', '.tsx', '.js', '.jsx', '.py', '.rs', '.go', '.java', '.css', '.html', '.json', '.yaml', '.yml', '.md', '.sql', '.sh', '.toml', '.env']

function syntaxHighlight(code: string): string {
  // Escape HTML - must be done first and completely
  let escaped = code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

  // We need to highlight without overlapping - use a token approach
  const tokens: { start: number; end: number; color: string; text: string }[] = []

  // Find all comments (multi-line first, then single-line)
  const multiCommentRegex = /\/\*[\s\S]*?\*\//g
  const singleCommentRegex = /\/\/.*$/gm
  const stringRegex = /(["'`])(?:(?=(\\?))\2.)*?\1/g
  const keywordRegex = /\b(import|export|from|default|const|let|var|function|async|await|return|if|else|switch|case|break|continue|for|while|do|try|catch|finally|throw|new|class|extends|implements|interface|type|enum|namespace|module|declare|as|in|of|typeof|instanceof|void|null|undefined|true|false|this|super|yield|readonly|keyof|infer|is|satisfies)\b/g
  const numberRegex = /\b(\d+\.?\d*)\b/g
  const jsxTagRegex = /(&lt;\/?[A-Z][a-zA-Z]*)/g
  const bracketRegex = /([{}[\]()])/g

  function addMatches(regex: RegExp, color: string, text: string) {
    let match
    const localRegex = new RegExp(regex.source, regex.flags)
    while ((match = localRegex.exec(text)) !== null) {
      tokens.push({
        start: match.index,
        end: match.index + match[0].length,
        color,
        text: match[0]
      })
    }
  }

  addMatches(multiCommentRegex, 'text-gray-500', escaped)
  addMatches(singleCommentRegex, 'text-gray-500', escaped)
  addMatches(stringRegex, 'text-green-400', escaped)
  addMatches(keywordRegex, 'text-purple-400', escaped)
  addMatches(numberRegex, 'text-orange-300', escaped)
  addMatches(jsxTagRegex, 'text-blue-400', escaped)
  addMatches(bracketRegex, 'text-yellow-300', escaped)

  // Sort by start position
  tokens.sort((a, b) => a.start - b.start)

  // Remove overlapping tokens (keep first one)
  const filtered: typeof tokens = []
  let lastEnd = 0
  for (const token of tokens) {
    if (token.start >= lastEnd) {
      filtered.push(token)
      lastEnd = token.end
    }
  }

  // Build final string with safe HTML
  let result = ''
  let pos = 0
  for (const token of filtered) {
    if (token.start > pos) {
      result += escaped.slice(pos, token.start)
    }
    // Use safe class name without injecting user content into HTML attributes
    result += `<span class="${token.color}">${token.text}</span>`
    pos = token.end
  }
  if (pos < escaped.length) {
    result += escaped.slice(pos)
  }

  return result
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleString('zh-CN')
  } catch {
    return '-'
  }
}

function isCodeFile(name: string): boolean {
  const ext = '.' + name.split('.').pop()?.toLowerCase()
  return CODE_EXTENSIONS.includes(ext)
}

function buildTree(files: FileInfo[]): TreeNode[] {
  const root: TreeNode[] = []
  const map = new Map<string, TreeNode>()

  // Sort files by path
  const sorted = [...files].sort((a, b) => a.path.localeCompare(b.path))

  for (const file of sorted) {
    const parts = file.path.split('/').filter(Boolean)
    let currentPath = ''
    let parentArr = root

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      currentPath = currentPath ? `${currentPath}/${part}` : part
      const isLast = i === parts.length - 1

      if (!map.has(currentPath)) {
        const node: TreeNode = {
          name: part,
          path: currentPath,
          type: isLast ? 'file' : 'folder',
          expanded: false,
          children: isLast ? undefined : [],
          size: isLast ? file.size : undefined,
          lastModified: isLast ? file.lastModified : undefined
        }
        map.set(currentPath, node)
        parentArr.push(node)
      }

      const node = map.get(currentPath)!
      if (node.children) {
        parentArr = node.children
      }
    }
  }

  return root
}

function FileTreeItem({
  node,
  depth,
  selectedPath,
  filter,
  onSelect
}: {
  node: TreeNode
  depth: number
  selectedPath: string | null
  filter: string
  onSelect: (path: string) => void
}) {
  const [expanded, setExpanded] = useState(depth < 2)

  const matchesFilter = !filter || node.name.toLowerCase().includes(filter.toLowerCase())
  const childrenMatch = node.children?.some(child =>
    child.name.toLowerCase().includes(filter.toLowerCase()) ||
    (child.children && child.children.some(gc => gc.name.toLowerCase().includes(filter.toLowerCase())))
  )
  const shouldShow = matchesFilter || childrenMatch

  if (!shouldShow) return null

  const isFolder = node.type === 'folder'
  const isSelected = node.path === selectedPath
  const isCode = !isFolder && isCodeFile(node.name)

  const handleClick = () => {
    if (isFolder) {
      setExpanded(!expanded)
    } else {
      onSelect(node.path)
    }
  }

  return (
    <div>
      <div
        className={`flex items-center gap-1 py-1 px-2 cursor-pointer text-sm rounded-sm transition-colors ${
          isSelected
            ? 'bg-orange-500/20 text-orange-400'
            : 'text-gray-300 hover:bg-gray-700'
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={handleClick}
      >
        {isFolder ? (
          <>
            {expanded ? <ChevronDown className="w-3 h-3 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 flex-shrink-0" />}
            <Folder className="w-4 h-4 flex-shrink-0 text-yellow-500" />
          </>
        ) : (
          <>
            <span className="w-3 flex-shrink-0" />
            <FileCode className={`w-4 h-4 flex-shrink-0 ${isCode ? 'text-blue-400' : 'text-gray-500'}`} />
          </>
        )}
        <span className="truncate">{node.name}</span>
      </div>
      {isFolder && expanded && node.children && (
        <div>
          {node.children.map((child, idx) => (
            <FileTreeItem
              key={`${child.path}-${idx}`}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              filter={filter}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function CodeViewer() {
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState<string>('')
  const [filter, setFilter] = useState('')
  const [copied, setCopied] = useState(false)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['storage-files'],
    queryFn: async () => {
      const [workflowsRes, longtasksRes] = await Promise.all([
        api.get('/storage/list', { params: { prefix: 'workflows/' } }).catch(() => ({ data: { data: [] } })),
        api.get('/storage/list', { params: { prefix: 'longtasks/' } }).catch(() => ({ data: { data: [] } }))
      ])
      const workflows = workflowsRes.data?.data || []
      const longtasks = longtasksRes.data?.data || []
      return [...workflows, ...longtasks]
    },
    refetchInterval: 30000
  })

  const files: FileInfo[] = useMemo(() => {
    if (!data) return []
    return data.map((item: any) => ({
      name: item.name || item.key?.split('/').pop() || '',
      path: item.key || item.path || item.name || '',
      size: item.size,
      lastModified: item.lastModified || item.modifiedAt || item.updatedAt
    }))
  }, [data])

  const tree = useMemo(() => buildTree(files), [files])

  const selectedFile = useMemo(() => {
    if (!selectedPath) return null
    return files.find(f => f.path === selectedPath) || null
  }, [files, selectedPath])

  const handleSelectFile = useCallback(async (path: string) => {
    setSelectedPath(path)
    setFileContent('')
    try {
      const res = await api.get('/storage/get', { params: { key: path } })
      setFileContent(res.data?.content || res.data?.data?.content || JSON.stringify(res.data, null, 2))
    } catch {
      setFileContent('Failed to load file content')
    }
  }, [])

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(fileContent).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [fileContent])

  const handleDownload = useCallback(() => {
    if (!selectedFile || !fileContent) return
    const blob = new Blob([fileContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = selectedFile.name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [selectedFile, fileContent])

  const highlightedCode = useMemo(() => {
    if (!fileContent) return ''
    return syntaxHighlight(fileContent)
  }, [fileContent])

  const lines = fileContent ? fileContent.split('\n') : []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Code className="w-7 h-7 text-orange-500" />
          <h2 className="text-2xl font-bold">Code Viewer</h2>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="flex gap-4" style={{ height: 'calc(100vh - 160px)' }}>
        {/* File Tree Sidebar */}
        <div className="w-72 bg-gray-800 rounded-lg border border-gray-700 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search files..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-orange-500"
              />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {files.length} files loaded
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {isLoading ? (
              <div className="flex items-center justify-center h-32 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                Loading files...
              </div>
            ) : files.length === 0 ? (
              <div className="text-center text-gray-500 py-8 text-sm">
                No files found
              </div>
            ) : (
              tree.map((node, idx) => (
                <FileTreeItem
                  key={`${node.path}-${idx}`}
                  node={node}
                  depth={0}
                  selectedPath={selectedPath}
                  filter={filter}
                  onSelect={handleSelectFile}
                />
              ))
            )}
          </div>
        </div>

        {/* Code Display Area */}
        <div className="flex-1 bg-gray-800 rounded-lg border border-gray-700 flex flex-col overflow-hidden">
          {selectedFile ? (
            <>
              {/* File Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-750">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <FileCode className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span className="font-medium text-sm truncate">{selectedFile.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{selectedFile.path}</div>
                </div>
                <div className="flex items-center gap-4 ml-4 flex-shrink-0">
                  <span className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</span>
                  <span className="text-xs text-gray-500">{formatDate(selectedFile.lastModified)}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopy}
                      className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className={`w-4 h-4 ${copied ? 'text-green-400' : 'text-gray-400'}`} />
                    </button>
                    <button
                      onClick={handleDownload}
                      className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                      title="Download file"
                    >
                      <Download className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Code Content */}
              <div className="flex-1 overflow-auto bg-gray-900">
                <div className="flex min-h-full">
                  {/* Line Numbers */}
                  <div className="flex-shrink-0 py-4 px-3 text-right select-none border-r border-gray-800 bg-gray-900">
                    {lines.map((_, idx) => (
                      <div key={idx} className="text-xs text-gray-600 leading-6 font-mono">
                        {idx + 1}
                      </div>
                    ))}
                  </div>

                  {/* Code */}
                  <div className="flex-1 py-4 px-4 overflow-x-auto">
                    <pre className="text-sm leading-6 font-mono text-gray-300">
                      <code dangerouslySetInnerHTML={{ __html: highlightedCode || '' }} />
                    </pre>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
              <Code className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg">Select a file to view</p>
              <p className="text-sm mt-2 text-gray-600">Choose a code file from the explorer</p>
            </div>
          )}
        </div>
      </div>

      {copied && (
        <div className="fixed bottom-6 right-6 bg-green-600 text-white px-4 py-2 rounded-lg text-sm shadow-lg">
          Copied to clipboard!
        </div>
      )}
    </div>
  )
}
